import { useRef, useState, DragEvent, ChangeEvent, CSSProperties } from 'react';
import { IconUpload, IconCamera } from '@tabler/icons-react';
import toast from 'react-hot-toast';
import { V4 } from '../../../tokens';
import { ACCEPTED_PDF_MIME, ACCEPTED_IMAGE_MIMES } from '../constants';
import { compressImages } from '../utils/compressImage';

export interface MultiFileUploadZoneProps {
  onFilesAdded: (files: File[]) => void;
  currentPdfCount: number;
  currentImageCount: number;
  currentTotalMB: number;
  maxPdfs: number;
  maxImages: number;
  maxFileMB: number;
  maxTotalMB: number;
  disabled?: boolean;
  /**
   * Modo compacto: cuando ya hay archivos seleccionados el dropzone se encoge
   * (menos alto, sin párrafo descriptivo) para que las thumbnails de abajo queden
   * dentro del viewport sin necesidad de hacer scroll. Default `false`.
   */
  compact?: boolean;
}

/*
 * Chip de límite con barra de progreso: hace VISIBLE cada tope (PDFs/Imágenes/
 * Total MB) y REACCIONA al agregar archivos animando el ancho de la barra
 * (transition width). El color escala verde→ámbar→rojo según qué tan cerca del
 * límite estás, así el usuario percibe de inmediato que su selección entró.
 */
function LimitChip({ label, valueText, ratio }: { label: string; valueText: string; ratio: number }) {
  const clamped = Math.min(1, Math.max(0, ratio));
  const fill = ratio >= 1 ? V4.danger : ratio >= 0.8 ? V4.warning : V4.secondary;
  const valueColor = ratio >= 1 ? V4.danger : ratio >= 0.8 ? V4.warningDark : V4.primary;

  return (
    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 6 }}>
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: V4.grayUltraDark,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </span>
        <span style={{ fontSize: 12, fontWeight: 700, color: valueColor, whiteSpace: 'nowrap' }}>{valueText}</span>
      </div>
      <div style={{ height: 4, borderRadius: 2, background: V4.grayLight, overflow: 'hidden' }}>
        <div
          style={{
            height: '100%',
            width: `${clamped * 100}%`,
            background: fill,
            borderRadius: 2,
            transition: 'width 260ms cubic-bezier(0.22, 1, 0.36, 1), background 200ms ease',
          }}
        />
      </div>
    </div>
  );
}

/*
 * Par de CTAs del dropzone (Elegir archivos / Tomar foto). Extraído a su propio
 * componente para mantener acotada la complejidad de MultiFileUploadZone y para
 * encapsular los estados de hover. En modo compacto encoge padding/label para que
 * ambos botones quepan en UNA fila incluso a 320px (sin empujar el grid fuera del
 * viewport).
 */
function UploadCtaButtons({
  compact,
  disabled,
  onPick,
  onCamera,
}: {
  compact: boolean;
  disabled: boolean;
  onPick: () => void;
  onCamera: () => void;
}) {
  const [primaryHover, setPrimaryHover] = useState(false);
  const [secondaryHover, setSecondaryHover] = useState(false);

  const sharedBtnStyle: CSSProperties = {
    borderRadius: 6,
    padding: compact ? '8px 14px' : '10px 20px',
    fontWeight: 700,
    fontSize: compact ? 12 : 13,
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: compact ? 6 : 8,
    transition: 'background 150ms ease',
  };

  const primaryBtnStyle: CSSProperties = {
    ...sharedBtnStyle,
    background: primaryHover && !disabled ? V4.secondaryDark : V4.secondary,
    color: V4.white,
    border: `1.5px solid ${primaryHover && !disabled ? V4.secondaryDark : V4.secondary}`,
  };

  const secondaryBtnStyle: CSSProperties = {
    ...sharedBtnStyle,
    background: secondaryHover && !disabled ? V4.grayUltraLight : V4.white,
    color: V4.primary,
    border: `1.5px solid ${V4.primary}`,
  };

  const iconSize = compact ? 14 : 16;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: 12,
        marginTop: 4,
      }}
    >
      <button
        type="button"
        data-testid="multifile-upload-cta-primary"
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          onPick();
        }}
        onMouseEnter={() => setPrimaryHover(true)}
        onMouseLeave={() => setPrimaryHover(false)}
        style={primaryBtnStyle}
      >
        <IconUpload size={iconSize} aria-hidden="true" />
        {compact ? 'Elegir' : 'Elegir archivos'}
      </button>

      <button
        type="button"
        data-testid="multifile-upload-cta-camera"
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          onCamera();
        }}
        onMouseEnter={() => setSecondaryHover(true)}
        onMouseLeave={() => setSecondaryHover(false)}
        style={secondaryBtnStyle}
      >
        <IconCamera size={iconSize} aria-hidden="true" />
        {compact ? 'Foto' : 'Tomar foto'}
      </button>
    </div>
  );
}

type AcceptedImageMime = (typeof ACCEPTED_IMAGE_MIMES)[number];

const ACCEPT_ATTR = [ACCEPTED_PDF_MIME, ...ACCEPTED_IMAGE_MIMES].join(',');

function isAcceptedMime(mime: string): boolean {
  if (mime === ACCEPTED_PDF_MIME) return true;
  return (ACCEPTED_IMAGE_MIMES as readonly string[]).includes(mime);
}

function isImageMime(mime: string): mime is AcceptedImageMime {
  return (ACCEPTED_IMAGE_MIMES as readonly string[]).includes(mime);
}

function bytesToMB(bytes: number): number {
  return bytes / (1024 * 1024);
}

export function MultiFileUploadZone({
  onFilesAdded,
  currentPdfCount,
  currentImageCount,
  currentTotalMB,
  maxPdfs,
  maxImages,
  maxFileMB,
  maxTotalMB,
  disabled = false,
  compact = false,
}: MultiFileUploadZoneProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const pdfRatio = maxPdfs > 0 ? currentPdfCount / maxPdfs : 0;
  const imageRatio = maxImages > 0 ? currentImageCount / maxImages : 0;
  const totalRatio = maxTotalMB > 0 ? currentTotalMB / maxTotalMB : 0;

  const processIncomingFiles = async (incoming: File[]): Promise<void> => {
    if (disabled) return;
    if (incoming.length === 0) return;

    // === Compresión client-side antes de validar tamaño ===
    // Reduce imágenes > 2MB a ~1.5MB target (cap 2400px lado mayor, JPEG q=0.82).
    // PDFs y archivos pequeños pasan intactos. Si el browser no soporta canvas,
    // o el navegador rechaza decodificar, también pasa intacto (degrade graceful).
    const compressionResults = await compressImages(incoming);
    let compressedCount = 0;
    let savedBytes = 0;
    const processed = compressionResults.map((r) => {
      if (r.compressed) {
        compressedCount += 1;
        savedBytes += r.originalBytes - r.finalBytes;
      }
      return r.file;
    });
    if (compressedCount > 0) {
      const savedMB = (savedBytes / (1024 * 1024)).toFixed(1);
      toast.success(
        `Optimizamos ${compressedCount} ${compressedCount === 1 ? 'imagen' : 'imágenes'} (${savedMB} MB menos) para acelerar.`,
        { id: 'smart-menu-compression' },
      );
    }

    const accepted: File[] = [];
    const rejectedByMime: string[] = [];
    const rejectedBySize: string[] = [];
    let pdfRoom = Math.max(0, maxPdfs - currentPdfCount);
    let imageRoom = Math.max(0, maxImages - currentImageCount);
    let totalMBLeft = Math.max(0, maxTotalMB - currentTotalMB);
    let droppedPdfCap = 0;
    let droppedImageCap = 0;
    let droppedTotalCap = 0;

    processed.forEach((file) => {
      if (!isAcceptedMime(file.type)) {
        rejectedByMime.push(file.name);
        return;
      }
      const sizeMB = bytesToMB(file.size);
      if (sizeMB > maxFileMB) {
        rejectedBySize.push(file.name);
        return;
      }

      const isPdf = file.type === ACCEPTED_PDF_MIME;
      if (isPdf) {
        if (pdfRoom <= 0) {
          droppedPdfCap += 1;
          return;
        }
      } else if (isImageMime(file.type)) {
        if (imageRoom <= 0) {
          droppedImageCap += 1;
          return;
        }
      }

      if (sizeMB > totalMBLeft) {
        droppedTotalCap += 1;
        return;
      }

      accepted.push(file);
      totalMBLeft -= sizeMB;
      if (isPdf) {
        pdfRoom -= 1;
      } else {
        imageRoom -= 1;
      }
    });

    if (rejectedByMime.length > 0) {
      toast.error(
        rejectedByMime.length === 1
          ? `Formato no soportado: ${rejectedByMime[0]}`
          : `${rejectedByMime.length} archivos con formato no soportado`,
      );
    }
    if (rejectedBySize.length > 0) {
      toast.error(
        rejectedBySize.length === 1
          ? `Archivo supera ${maxFileMB} MB: ${rejectedBySize[0]}`
          : `${rejectedBySize.length} archivos superan ${maxFileMB} MB`,
      );
    }
    if (droppedPdfCap > 0) {
      toast(`Solo aceptamos ${maxPdfs} PDFs. ${droppedPdfCap} quedaron fuera.`);
    }
    if (droppedImageCap > 0) {
      toast(`Solo aceptamos ${maxImages} imágenes. ${droppedImageCap} quedaron fuera.`);
    }
    if (droppedTotalCap > 0) {
      toast(`Límite total ${maxTotalMB} MB alcanzado. ${droppedTotalCap} archivos quedaron fuera.`);
    }

    if (accepted.length > 0) {
      onFilesAdded(accepted);
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (disabled) return;
    const files = Array.from(e.dataTransfer.files);
    processIncomingFiles(files).catch(() => undefined);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragOver(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    processIncomingFiles(files).catch(() => undefined);
    e.target.value = '';
  };

  const openFilePicker = (): void => {
    if (!disabled) fileInputRef.current?.click();
  };

  const openCameraPicker = (): void => {
    if (!disabled) cameraInputRef.current?.click();
  };

  const dropzoneBorder = disabled ? V4.grayLight : isDragOver ? V4.secondary : V4.grayLight;

  const dropzoneStyle: CSSProperties = {
    border: `2px dashed ${dropzoneBorder}`,
    background: isDragOver && !disabled ? V4.secondaryFaded : V4.grayUltraLight,
    borderRadius: 12,
    padding: compact ? 16 : 32,
    minHeight: compact ? 92 : 200,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    transition: 'border-color 150ms ease, background 150ms ease, min-height 200ms ease, padding 200ms ease',
    gap: compact ? 8 : 12,
  };

  const counterAriaLabel = `PDFs ${currentPdfCount}/${maxPdfs} · Imágenes ${currentImageCount}/${maxImages} · Total ${currentTotalMB.toFixed(
    1,
  )}/${maxTotalMB} MB`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div
        data-testid="multifile-upload-counter"
        aria-label={counterAriaLabel}
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: 12,
          width: '100%',
        }}
      >
        <LimitChip label="Imágenes" valueText={`${currentImageCount}/${maxImages}`} ratio={imageRatio} />
        <LimitChip label="PDFs" valueText={`${currentPdfCount}/${maxPdfs}`} ratio={pdfRatio} />
        <LimitChip label="Total" valueText={`${currentTotalMB.toFixed(1)}/${maxTotalMB} MB`} ratio={totalRatio} />
      </div>

      <div
        data-testid="multifile-upload-zone"
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        aria-label="Zona para subir archivos del menú"
        onClick={openFilePicker}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openFilePicker();
          }
        }}
        onDragEnter={handleDragOver}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={dropzoneStyle}
      >
        <IconUpload size={compact ? 26 : 40} color={V4.secondary} stroke={1.6} aria-hidden="true" />

        <h3
          style={{
            margin: 0,
            fontSize: compact ? 15 : 18,
            fontWeight: 700,
            color: V4.primary,
          }}
        >
          {compact ? 'Agrega más archivos' : 'Sube tu menú actual'}
        </h3>

        {compact ? null : (
          <p
            style={{
              margin: 0,
              fontSize: 14,
              color: V4.grayUltraDark,
              maxWidth: 460,
              lineHeight: 1.4,
            }}
          >
            Lo digitalizamos por ti en segundos. Suelta fotos del menú impreso o el PDF que ya tengas. Hasta {maxPdfs}{' '}
            PDFs y {maxImages} imágenes.
          </p>
        )}

        <UploadCtaButtons compact={compact} disabled={disabled} onPick={openFilePicker} onCamera={openCameraPicker} />

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPT_ATTR}
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
          disabled={disabled}
          aria-hidden="true"
          tabIndex={-1}
        />

        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
          disabled={disabled}
          aria-hidden="true"
          tabIndex={-1}
        />
      </div>
    </div>
  );
}
